import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import TuneIcon from '@mui/icons-material/Tune';
import { debugError } from '../../../utils/debugLog';
import { useThemeManagement } from '../hooks/useThemeManagement';
import InlineThemePreview from './InlineThemePreview';
import PresetCard from './PresetCard';
import { SPACING, THEME_PRESETS } from '../../../theme';
import {
  TabPanel,
  ColorPickerSection,
  TypographySection,
  LayoutSection,
  CustomSection,
  AdvancedSection,
  ThemeActions,
} from './ThemeCustomizerComponents';

// Main Theme Customizer component
function ThemeCustomizer({ theme, onBack, onSave }) {
  const {
    themes,
    loading,
    previewMode,
    setPreviewMode,
    fetchThemes,
    handleSubmit,
  } = useThemeManagement();

  const [themeMode, setThemeMode] = useState('simple'); // 'simple' or 'complex'
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [themeData, setThemeData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Initialize theme data from the passed theme or use default empty theme for new themes
  useEffect(() => {
    if (theme) {
      setThemeData(theme);
    } else {
      // For new themes, start with empty/default theme data so presets can be selected
      setThemeData({
        theme_name: '',
        theme_description: '',
        primary_color: '#0A84FF',
        secondary_color: '#2563EB',
        accent_color: '#3B82F6',
        background_color: '#F5F7FA',
        surface_color: '#FFFFFF',
        text_color: '#1A1A1A',
        success_color: '#27AE60',
        warning_color: '#F59E0B',
        error_color: '#EF4444',
        info_color: '#3B82F6',
        font_family: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
        font_size: '18px', // Increased from 14px for better readability
        border_radius: 8,
        spacing_unit: 8,
        mode: 'light',
      });
    }
  }, [theme, themes]);

  // Handle mode change
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setThemeMode(newMode);
      // When switching to complex mode, maintain selected preset if any
      if (newMode === 'complex' && selectedPreset) {
        setNotification({
          open: true,
          message: `Switched to Complex Mode. You can now fine-tune "${selectedPreset}".`,
          severity: 'info',
        });
      }
    }
  };

  // Handle preset selection
  const applyPreset = preset => {
    setSelectedPreset(preset.theme_name);
    setThemeData({ ...preset });
    setNotification({
      open: true,
      message: `Applied "${preset.theme_name}" preset. Click Save to keep changes.`,
      severity: 'info',
    });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Update theme data
  const updateThemeData = (path, value) => {
    setThemeData(prevData => {
      const newData = { ...prevData };

      // Handle direct property updates (no nested paths)
      if (!path.includes('.')) {
        newData[path] = value;
        return newData;
      }

      // Handle nested property updates
      const pathParts = path.split('.');
      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newData;
    });
  };

  // Save theme
  const saveTheme = async () => {
    try {
      setSaving(true);

      // Validate required fields
      if (
        !themeData ||
        !themeData.theme_name ||
        themeData.theme_name.trim() === ''
      ) {
        throw new Error('Theme name is required');
      }

      if (onSave) {
        // Use the passed onSave callback
        await onSave(themeData);
      } else {
        // Fallback to existing handleSubmit from useThemeManagement
        await handleSubmit(themeData);
      }

      setNotification({
        open: true,
        message: 'Theme saved successfully',
        severity: 'success',
      });
    } catch (error) {
      debugError('Error saving theme:', error);
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      setNotification({
        open: true,
        message: `Failed to save theme: ${errorMessage}`,
        severity: 'error',
      });
      // Re-throw so parent can handle it if needed
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Theme Customizer...
        </Typography>
      </Box>
    );
  }

  // If no theme data, show loading or error
  if (!themeData) {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading theme data...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load theme data. Please try again later.
        </Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={fetchThemes}>
          Refresh Themes
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: SPACING.xxl }}>
      {/* Header */}
      <ThemeActions
        theme={theme}
        onBack={onBack}
        onRefresh={fetchThemes}
        onSave={saveTheme}
        saving={saving}
      />

      {/* Mode Toggle */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={themeMode}
          exclusive
          onChange={handleModeChange}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="simple">
            <PaletteIcon sx={{ mr: 1 }} />
            Simple Mode - Choose a Preset
          </ToggleButton>
          <ToggleButton value="complex">
            <TuneIcon sx={{ mr: 1 }} />
            Complex Mode - Customize Details
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Preview toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={previewMode}
              onChange={e => setPreviewMode(e.target.checked)}
              color="primary"
            />
          }
          label="Live Preview"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Theme editor */}
        <Grid size={{ xs: 12, md: previewMode ? 6 : 12 }}>
          {/* Simple Mode - Preset Selection */}
          {themeMode === 'simple' && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Choose a Theme Preset
              </Typography>
              <Grid container spacing={3}>
                {THEME_PRESETS.map(preset => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={preset.theme_name}>
                    <PresetCard
                      preset={preset}
                      isSelected={selectedPreset === preset.theme_name}
                      onClick={() => applyPreset(preset)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Complex Mode - Detailed Customization */}
          {themeMode === 'complex' && (
            <>
              {/* Show which preset is being edited, if any */}
              {selectedPreset && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Customizing "{selectedPreset}" preset. Make your changes
                  below.
                </Alert>
              )}

              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Colors" />
                  <Tab label="Typography" />
                  <Tab label="Components" />
                  <Tab label="Custom" />
                  <Tab label="Advanced" />
                </Tabs>

                {/* Colors Tab */}
                <TabPanel value={activeTab} index={0}>
                  <ColorPickerSection
                    themeData={themeData}
                    updateThemeData={updateThemeData}
                  />
                </TabPanel>

                {/* Typography Tab */}
                <TabPanel value={activeTab} index={1}>
                  <TypographySection
                    themeData={themeData}
                    updateThemeData={updateThemeData}
                  />
                </TabPanel>

                {/* Components Tab */}
                <TabPanel value={activeTab} index={2}>
                  <LayoutSection
                    themeData={themeData}
                    updateThemeData={updateThemeData}
                  />
                </TabPanel>

                {/* Custom Tab */}
                <TabPanel value={activeTab} index={3}>
                  <CustomSection
                    themeData={themeData}
                    updateThemeData={updateThemeData}
                  />
                </TabPanel>

                {/* Advanced Tab */}
                <TabPanel value={activeTab} index={4}>
                  <AdvancedSection
                    themeData={themeData}
                    setThemeData={setThemeData}
                  />
                </TabPanel>
              </Paper>
            </>
          )}
        </Grid>

        {/* Inline Preview area */}
        {previewMode && (
          <Grid size={{ xs: 12, md: 6 }}>
            <InlineThemePreview theme={themeData} />
          </Grid>
        )}
      </Grid>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ThemeCustomizer;
